import {
  loadServiceCalibPinImages,
  registerServiceCalibStyleImageMissingHandler,
  resolveStationMapIconIdFromLots,
  SERVICE_CALIB_MAP_ICON_SIZE_LAYOUT,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibMapIcons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Layer, Source, SymbolLayer } from 'react-map-gl'
import { useSelector } from 'react-redux'
import { selectHighlightedFeature } from 'store/selectors/mission'
import { selectLots, selectStation } from 'store/selectors/station'
import { IRobotOnMap } from 'utils/interfaces'
import { centerMapTo, zoomMapTo } from 'utils/mapUtils'

import CenterOnStationButton from './CenterOnStationButton/CenterOnStationButton'

/** Initial fit for a single station pin — default zoomMapTo maxZoom 21 is too tight. */
const STATION_MAP_INITIAL_MAX_ZOOM = 17

const layerStationStyle: any = {
  id: 'station-image-style',
  type: 'symbol',
  layout: {
    'icon-image': ['get', 'iconId'],
    'icon-anchor': 'center',
    'icon-rotation-alignment': 'map',
    'icon-size': SERVICE_CALIB_MAP_ICON_SIZE_LAYOUT,
    'icon-allow-overlap': true,
  },
  paint: {
    'icon-opacity': 1,
    'icon-opacity-transition': { delay: 0, duration: 0 },
  },
}

const StationOnMap = ({
  map,
  offset = 0,
}: {
  map: any | undefined
  /** Left floating panel inset (px), same as RobotOnMap / GEO_MAP_LEFT_PANEL_INSET_PX. */
  offset?: number
}) => {
  const station = useSelector(selectStation)
  const lots = useSelector(selectLots)

  const stationLots = useMemo(
    () => lots.filter(lot => lot.robot_station === station?.id),
    [lots, station?.id]
  )
  const iconId = useMemo(() => resolveStationMapIconIdFromLots(stationLots), [stationLots])

  const [stationStyle, setStationStyle] = useState<SymbolLayer>(layerStationStyle)
  const [stationOnMap, setStationOnMap] = useState<IRobotOnMap>()
  const [stationZoomed, setStationZoomed] = useState(false)
  const [stationCenteringMode, setStationCenteringMode] = useState(true)
  const [centeringNonce, setCenteringNonce] = useState(0)
  const lastCenteringCoords = useRef<[number, number] | null>(null)
  const highlightedFeature = useSelector(selectHighlightedFeature)

  useEffect(() => {
    if (!station?.point?.coordinates || station.point.coordinates.length < 2) {
      setStationOnMap(undefined)
      return
    }
    setStationOnMap({
      id: 'station_on_map',
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [station.point.coordinates[0], station.point.coordinates[1]],
      },
      properties: {
        yaw: 0,
        iconId,
      },
    })
  }, [station, iconId])

  // Same camera sequence as RobotOnMap: initial zoomMapTo, then centerMapTo only when needed
  // (not immediately after zoom — that would clamp maxZoom to the still-far default zoom).
  useEffect(() => {
    if (!stationOnMap || !map) {
      return
    }

    const coords = stationOnMap.geometry.coordinates as [number, number]

    if (!stationZoomed) {
      zoomMapTo(map, [stationOnMap], offset, STATION_MAP_INITIAL_MAX_ZOOM)
      setStationZoomed(true)
      lastCenteringCoords.current = coords
      return
    }

    if (stationCenteringMode) {
      let shouldCenter = !lastCenteringCoords.current
      if (lastCenteringCoords.current) {
        const [prevLng, prevLat] = lastCenteringCoords.current
        const moved =
          Math.abs(prevLng - coords[0]) > 1e-7 || Math.abs(prevLat - coords[1]) > 1e-7
        if (moved) {
          shouldCenter = true
        }
      }

      if (shouldCenter) {
        centerMapTo(map, [stationOnMap], offset)
        lastCenteringCoords.current = coords
      }
    }
  }, [stationZoomed, stationOnMap, map, stationCenteringMode, offset, centeringNonce])

  useEffect(() => {
    if (highlightedFeature) {
      setStationCenteringMode(false)
    }
  }, [highlightedFeature])

  useEffect(() => {
    if (!map) {
      return
    }
    void loadServiceCalibPinImages(map)
    const unregister = registerServiceCalibStyleImageMissingHandler(map)
    const onStyleLoad = () => {
      void loadServiceCalibPinImages(map)
    }
    const onDragStart = () => setStationCenteringMode(false)
    map.on('style.load', onStyleLoad)
    map.on('dragstart', onDragStart)
    return () => {
      unregister()
      map.off('style.load', onStyleLoad)
      map.off('dragstart', onDragStart)
    }
  }, [map])

  useEffect(() => {
    // Online / no_feedback: full opacity. Offline: slightly dimmed (not “dead”).
    const feedback = station.feedback_status
    const dimOffline = feedback === 'offline' || (feedback == null && station.online !== true)
    if (dimOffline) {
      setStationStyle({
        ...layerStationStyle,
        paint: {
          ...layerStationStyle.paint,
          'icon-opacity': 0.55,
        },
      })
    } else {
      setStationStyle(layerStationStyle)
    }
  }, [station.online, station.feedback_status])

  return (
    <>
      {stationOnMap && (
        <Source id="station" type="geojson" data={stationOnMap}>
          <Layer {...stationStyle} />
        </Source>
      )}
      <CenterOnStationButton
        onClick={() => {
          setStationCenteringMode(true)
          setCenteringNonce(n => n + 1)
          lastCenteringCoords.current = null
        }}
      />
    </>
  )
}

export default StationOnMap
